import { Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { SearchableText } from "../../../../../shared/components/search/Searchable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { formatTimeWindow } from "../meetingRoomsLib"
import type { MeetingRoomBookingItem, MeetingRoomRoomGroup } from "../meetingRoomsTypes"
import { MeetingRoomBookingRow } from "./MeetingRoomBookingRow"
import { MeetingRoomsGroup } from "./MeetingRoomsGroup"
import { MeetingRoomsGroupsSkeleton } from "./MeetingRoomsGroupsSkeleton"
import { MeetingRoomsRoomGroupsHeader } from "./MeetingRoomsRoomGroupsHeader"

const useStyles = makeStyles()(() => ({
    noMatch: {
        padding: "3.5rem 1.5rem",
        textAlign: "center",
    },
}))

type Props = Readonly<{
    isLoading: boolean
    groups: ReadonlyArray<MeetingRoomRoomGroup>
    searchRegex?: RegExp
    onOpen: (item: MeetingRoomBookingItem) => void
}>

export function MeetingRoomsRoomGroups({ isLoading, groups, searchRegex, onOpen }: Props) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <>
            <MeetingRoomsRoomGroupsHeader />
            {isLoading && <MeetingRoomsGroupsSkeleton />}
            {!isLoading && !groups.length && (
                <LocalizedStrict id="service-center-meeting-rooms-table-no-match">
                    <Typography variant="body1" color="text.secondary" className={classes.noMatch}>
                        No rooms match the filters — reset status, floor or search.
                    </Typography>
                </LocalizedStrict>
            )}
            {!isLoading &&
                groups.map(({ room, items }) => (
                    <MeetingRoomsGroup
                        key={room.id}
                        label={<SearchableText search={searchRegex}>{room.name}</SearchableText>}
                        meta={
                            room.seats !== undefined ? (
                                <LocalizedStrict
                                    id="service-center-meeting-rooms-table-room-seats"
                                    vars={{ floor: room.floorName, seats: room.seats }}
                                >
                                    <span>
                                        {room.floorName} · {room.seats}
                                    </span>
                                </LocalizedStrict>
                            ) : (
                                room.floorName
                            )
                        }
                        isEmpty={!items.length}
                        emptyText={
                            <LocalizedStrict id="service-center-meeting-rooms-group-room-empty">
                                <span>No bookings today</span>
                            </LocalizedStrict>
                        }
                    >
                        {items.map((item) => (
                            <MeetingRoomBookingRow
                                key={item.booking.orderId}
                                item={item}
                                lead={formatTimeWindow(dateTimeFactory, item.timeRange)}
                                trailing={dateTimeFactory.formatDuration(
                                    dateTimeFactory
                                        .fromMillis(item.timeRange.endMillis)
                                        .diff(dateTimeFactory.fromMillis(item.timeRange.startMillis), [
                                            "hours",
                                            "minutes",
                                        ]),
                                )}
                                searchRegex={searchRegex}
                                onOpen={onOpen}
                            />
                        ))}
                    </MeetingRoomsGroup>
                ))}
        </>
    )
}
